import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const CACHE_PREFIX = "profilePic:";

// In-memory cache — keeps the picture instantly available when the sidebar
// remounts (every page/view switch) without refetching from the API.
let memoryCache = { code: null, url: null };

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const readSessionCache = (code) => {
  try {
    return sessionStorage.getItem(CACHE_PREFIX + code) || null;
  } catch (e) {
    return null;
  }
};

const writeCaches = (code, dataUrl) => {
  memoryCache = { code, url: dataUrl };
  try {
    sessionStorage.setItem(CACHE_PREFIX + code, dataUrl);
  } catch (e) {
    // Quota exceeded (very large image) — the in-memory cache still covers
    // this session; only a full refresh would need to refetch.
  }
};

// Fetch the picture and warm the caches.
// Called fire-and-forget right after login, and by the hook below.
export const prefetchProfilePicture = async (code) => {
  if (!code) return null;
  try {
    const response = await apiFetch(`${BASE_URL}/employees/pic/${code}`, {
      method: "GET",
    });
    if (!response.ok) return null;

    const blob = await response.blob();
    if (!blob || blob.size === 0) return null;

    const dataUrl = await blobToDataUrl(blob);
    writeCaches(code, dataUrl);
    return dataUrl;
  } catch (err) {
    console.error("Error loading profile pic:", err);
    return null;
  }
};

// ===== PROFILE PICTURE (fetch + upload) =====
export const useProfilePicture = ({ profile, language, t, showAlert }) => {
  // localStorage fallback makes the code available immediately after login,
  // before the profile context has finished loading.
  const infinityCode =
    profile?.infinityCode || localStorage.getItem("infinityCode") || null;

  const [profilePic, setProfilePic] = useState(() => {
    if (!infinityCode) return null;
    if (memoryCache.code === infinityCode && memoryCache.url) {
      return memoryCache.url;
    }
    return readSessionCache(infinityCode);
  });
  const [isUploadingProfilePic, setIsUploadingProfilePic] = useState(false);
  const profilePicInputRef = useRef(null);

  useEffect(() => {
    if (!infinityCode) return;

    // Serve instantly from cache; only hit the network when nothing is cached
    if (memoryCache.code === infinityCode && memoryCache.url) {
      setProfilePic(memoryCache.url);
      return;
    }

    const cached = readSessionCache(infinityCode);
    if (cached) {
      memoryCache = { code: infinityCode, url: cached };
      setProfilePic(cached);
      return;
    }

    let cancelled = false;
    prefetchProfilePicture(infinityCode).then((url) => {
      if (!cancelled && url) setProfilePic(url);
    });
    return () => {
      cancelled = true;
    };
  }, [infinityCode]);

  const handleProfilePicUpload = async (file) => {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      showAlert(t.error || "Error", language === 'ar' ? "يرجى اختيار ملف صورة" : "Please select an image file", 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showAlert(t.error || "Error", language === 'ar' ? "حجم الملف يجب أن يكون أقل من 5 ميجابايت" : "File size must be less than 5MB", 'error');
      return;
    }

    setIsUploadingProfilePic(true);

    try {
      const formData = new FormData();
      formData.append("profilePicture", file);

      const response = await apiFetch(
        `${BASE_URL}/employees/pic/load/${infinityCode}`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (response.ok) {
        // Reload the picture through the prefetch so the caches update too
        const url = await prefetchProfilePicture(infinityCode);
        if (url) setProfilePic(url);
        showAlert(t.success || "Success", language === 'ar' ? "تم تحديث صورة الملف الشخصي بنجاح!" : "Profile picture updated successfully!", 'success');
      } else {
        // The server's own (bilingual) explanation when it gives one —
        // never a status code or a raw response body.
        const body = await response.json().catch(() => null);
        console.error("Upload failed:", response.status, body);
        showAlert(
          t.error || "Error",
          body?.messages?.[language] ||
            (language === 'ar' ? "تعذّر رفع الصورة. يرجى المحاولة مرة أخرى." : "The photo couldn't be uploaded. Please try again."),
          'error'
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      showAlert(
        t.error || "Error",
        // ConnectionError already carries a clear bilingual message.
        error?.isConnectionError
          ? error.message
          : (language === 'ar' ? "تعذّر رفع الصورة. يرجى المحاولة مرة أخرى." : "The photo couldn't be uploaded. Please try again."),
        'error'
      );
    } finally {
      setIsUploadingProfilePic(false);
    }
  };

  const handleProfilePicSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleProfilePicUpload(file);
    }
  };

  const triggerProfilePicInput = () => {
    profilePicInputRef.current?.click();
  };

  return {
    profilePic,
    // Only accounts linked to an employee have a picture to change.
    canChangeProfilePic: !!infinityCode,
    isUploadingProfilePic,
    profilePicInputRef,
    handleProfilePicSelect,
    triggerProfilePicInput,
  };
};
